import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ShieldAlert, ArrowLeft, Smartphone, MonitorDot, UserCheck, CheckCircle2,
  Siren, MapPin, Phone, Volume2, Radio, Bell, Pencil,
} from "lucide-react";

const Step = ({ n, icon: Icon, title, children, color }) => (
  <div className="relative pl-16 pb-10 last:pb-0">
    <div className="absolute left-0 top-0 flex flex-col items-center h-full">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 w-px bg-slate-800 mt-2" />
    </div>
    <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="font-mono text-xs text-slate-500">ÉTAPE {n}</span>
        <h3 className="font-display font-semibold text-slate-100 text-lg">{title}</h3>
      </div>
      <div className="text-sm text-slate-300 leading-relaxed space-y-2">{children}</div>
    </div>
  </div>
);

const Chip = ({ children, className }) => (
  <span className={`inline-flex items-center gap-1 font-mono text-[11px] px-2 py-0.5 rounded-md border ${className}`}>{children}</span>
);

const Guide = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100">
      {/* Header */}
      <header className="h-16 sticky top-0 z-40 backdrop-blur-md bg-slate-900/80 border-b border-slate-800 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-500/30 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="font-display font-bold leading-tight">Guide d'utilisation</h1>
            <p className="text-[11px] font-mono text-slate-500">Sama Jaamu — Dispositif d'alerte et de sécurité</p>
          </div>
        </div>
        <Button data-testid="guide-back-btn" onClick={() => navigate("/")} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour au dashboard
        </Button>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        {/* Intro */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-1 w-6 rounded-full bg-emerald-600" />
            <span className="h-1 w-6 rounded-full bg-yellow-500" />
            <span className="h-1 w-6 rounded-full bg-red-600" />
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-3">Comment fonctionne Sama Jaamu</h2>
          <p className="text-slate-400 leading-relaxed max-w-2xl">
            Sama Jaamu relie les citoyens en danger au centre de supervision Sama Jaamu. Ce guide décrit le
            parcours complet d'une alerte : du déclenchement par un citoyen depuis l'application mobile,
            jusqu'au traitement par le superviseur sur ce tableau de bord.
          </p>
        </div>

        {/* Steps */}
        <div>
          <Step n={1} icon={Smartphone} title="Le citoyen déclenche une alerte SOS" color="bg-red-950/40 border-red-500/40 text-red-400">
            <p>
              Depuis l'application mobile <strong>Sama Jaamu</strong>, le citoyen en situation de danger appuie sur le
              bouton <Chip className="bg-red-500/10 text-red-400 border-red-500/30"><Siren className="w-3 h-3" /> SOS</Chip>.
            </p>
            <p>L'application transmet automatiquement au Centre :</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-400">
              <li>son <strong>nom</strong> et son <strong>numéro de téléphone</strong> ;</li>
              <li>sa <strong>position GPS</strong> en temps réel (mise à jour pendant son déplacement) ;</li>
              <li>éventuellement une courte <strong>vidéo</strong> de la situation.</li>
            </ul>
            <p className="text-slate-400">L'alerte est alors transmise instantanément au tableau de bord superviseur.</p>
          </Step>

          <Step n={2} icon={MonitorDot} title="Le superviseur reçoit et suit l'alerte" color="bg-emerald-950/40 border-emerald-500/40 text-emerald-400">
            <p>
              À l'arrivée d'une nouvelle alerte, le dashboard réagit immédiatement :
            </p>
            <ul className="list-disc pl-5 space-y-1 text-slate-400">
              <li>un <Chip className="bg-red-500/10 text-red-400 border-red-500/30"><Bell className="w-3 h-3" /> bandeau rouge clignotant</Chip> s'affiche en haut de l'écran ;</li>
              <li>une <Chip className="bg-red-500/10 text-red-400 border-red-500/30"><Volume2 className="w-3 h-3" /> sirène sonore</Chip> se déclenche (activable/désactivable via le bouton son du header) ;</li>
              <li>un <Chip className="bg-red-500/10 text-red-400 border-red-500/30"><MapPin className="w-3 h-3" /> marqueur rouge</Chip> apparaît sur la carte, centrée sur le Sénégal.</li>
            </ul>
            <p>
              Le superviseur clique sur l'alerte (dans la liste de gauche ou sur la carte) pour ouvrir son détail :
              nom, <Chip className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30"><Phone className="w-3 h-3" /> téléphone</Chip>,
              coordonnées GPS, précision, et le <strong className="text-red-400">tracé du déplacement du client en rouge</strong> sur la carte. L'agent assigné apparaît, lui, <strong className="text-blue-400">en bleu</strong>.
            </p>
            <p className="text-slate-400 flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5 text-emerald-400" /> La liste se rafraîchit automatiquement toutes les 5 secondes.
            </p>
          </Step>

          <Step n={3} icon={UserCheck} title="Assigner un agent à l'intervention" color="bg-amber-950/40 border-amber-500/40 text-amber-400">
            <p>Dans le panneau de détail de l'alerte, à la rubrique <strong>« Agent assigné »</strong> :</p>
            <ol className="list-decimal pl-5 space-y-1 text-slate-400">
              <li>saisir le nom de l'agent ou de l'équipe d'intervention dans le champ prévu ;</li>
              <li>cliquer sur le bouton <Chip className="bg-yellow-500/10 text-yellow-400 border-yellow-500/30">Assigner</Chip>.</li>
            </ol>
            <p>
              L'alerte passe alors au statut <Chip className="bg-amber-500/10 text-amber-400 border-amber-500/30">En cours</Chip> ;
              son marqueur devient <span className="text-yellow-400 font-semibold">or</span> sur la carte et elle est comptabilisée
              dans l'indicateur « En cours ». Le superviseur peut aussi ajouter des
              <Chip className="bg-slate-500/10 text-slate-300 border-slate-600"><Pencil className="w-3 h-3" /> notes d'intervention</Chip> pour tracer les actions menées.
            </p>
          </Step>

          <Step n={4} icon={CheckCircle2} title="Marquer l'alerte comme traitée" color="bg-emerald-950/40 border-emerald-500/40 text-emerald-400">
            <p>
              Une fois l'intervention terminée et le citoyen pris en charge, le superviseur clique sur le bouton
              <Chip className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 ml-1"><CheckCircle2 className="w-3 h-3" /> Marquer comme traitée</Chip>
              situé en bas du panneau de détail.
            </p>
            <p>
              L'alerte est alors clôturée : elle disparaît de la liste des alertes actives et vient incrémenter le
              compteur <Chip className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">Traitées</Chip> de la barre de statistiques.
              Le tableau de bord reste ainsi concentré sur les situations en cours.
            </p>
          </Step>
        </div>

        {/* Footer note */}
        <div className="mt-8 bg-slate-900/60 border border-slate-800 rounded-xl p-5 text-center">
          <p className="text-sm text-slate-400">
            En cas de doute sur une alerte, contactez directement le citoyen via son numéro de téléphone
            affiché dans le détail de l'alerte avant d'engager une intervention.
          </p>
          <p className="mt-3 text-xs font-mono text-slate-600">Sama Jaamu — Dispositif d'alerte et de sécurité</p>
        </div>
      </main>
    </div>
  );
};

export default Guide;
